import { TriangleIcon } from "@imi/icons/TriangleIcon.js";
import {
  Dropzone,
  DropzoneContent,
  DropzoneDragging,
  DropzoneLoading,
} from "@imi/ui/components/Dropzone/Dropzone.js";
import { FieldError } from "@imi/ui/components/FieldError/FieldError.js";
import { LoadingSpinner } from "@imi/ui/components/LoadingSpinner/LoadingSpinner.js";
import { useConfirm } from "@imi/ui/hooks/useConfirm.js";
import { useWaterCharacteristicsPreference } from "@src/core/api/useWaterCharacteristicsPreference";
import { EngineWorkerContext } from "@src/core/engine/workers/contexts/EngineWorkerContext";
import { useAppDispatch } from "@src/core/store";
import { useBuildSuggestionOutput } from "@src/routes/projects/$projectId/selections/hooks/useBuildSuggestionOutput";
import { useGetProductDescriptions } from "@src/shared/components/ProductDetailsCard/hooks/useGetProductDescriptions";
import { useToast } from "@src/shared/components/Toast/useToast";
import {
  isAbortError,
  isReadFileError,
  readFileAsArrayBuffer,
} from "@src/shared/helpers/files";
import { useUnitConversion } from "@src/shared/hooks/useUnitConversion";
import { HeaderContext, TableFeatures } from "@tanstack/react-table";
import Excel from "exceljs";
import { isEmpty, isString, tryit, unique } from "radashi";
import { use, useCallback, useState } from "react";
import { flushSync } from "react-dom";
import { useDropzone } from "react-dropzone";
import { Trans, useTranslation } from "react-i18next";
import { BatchSuggestionsContext } from "../../../../contexts/BatchSuggestionsContext";
import { useCategoryId } from "../../../../hooks/useCategoryId";
import { replaceAllValues } from "../../../../store/replaceAllValues";
import { batchInputColumns, BatchSelectionRow } from "../../../../store/types";
import { isParseXlsxDataError, parseXlsxData } from "./helpers/parseXlsxData";
import styles from "./ImportDropzone.module.css";
import { ImportXlsxIcon } from "./ImportXlsxIcon";

type ImportDropzoneProps = {
  header: HeaderContext<TableFeatures, BatchSelectionRow, unknown>;
  setUseFlowOrPower: (value: string) => void;
  useFlowOrPower: string;
};

export function ImportDropzone({
  header,
  setUseFlowOrPower,
  useFlowOrPower,
}: ImportDropzoneProps) {
  const { t } = useTranslation("product", {
    keyPrefix: "selection.batch.import",
  });
  const [confirm, dialog] = useConfirm();
  const [loading, setLoading] = useState(false);
  const categoryId = useCategoryId();
  const { convertToInternationalUnit, convertToUserUnit, units } =
    useUnitConversion();
  const waterCharacteristics = useWaterCharacteristicsPreference();
  const waterCharacteristicsData =
    waterCharacteristics.status === "success"
      ? waterCharacteristics.data
      : undefined;
  const { waitUntilReady } = use(EngineWorkerContext);
  const { fetchSuggestions } = use(BatchSuggestionsContext);
  const { addToast } = useToast();
  const dispatch = useAppDispatch();
  const buildSuggestionOutput = useBuildSuggestionOutput();
  const { getProductDescriptions } = useGetProductDescriptions();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (waterCharacteristicsData === undefined) return;
      const file = acceptedFiles[0];
      if (!file) {
        return addToast({
          message: t("errors.invalidFileType.message"),
          title: t("errors.invalidFileType.title"),
        });
      }

      setLoading(true);
      const [error, result] = await tryit(readFileAsArrayBuffer)(file);
      if (isAbortError(error)) {
        return addToast({
          message: t("errors.fileUploadAbort.message"),
          title: t("errors.fileUploadAbort.title"),
        });
      }

      if (isReadFileError(error)) {
        setLoading(false);
        return addToast({
          message: t("errors.fileUploadError.message"),
          title: t("errors.fileUploadError.title"),
        });
      }

      const fileContent = result;
      if (!fileContent || isString(fileContent)) {
        setLoading(false);
        return addToast({
          message: t("errors.fileReadError.message"),
          title: t("errors.fileReadError.title"),
        });
      }

      const workbook = new Excel.Workbook();
      await workbook.xlsx.load(fileContent);
      const [parsingError, parsedData] = tryit(parseXlsxData)(workbook, units);

      if (parsingError) {
        setLoading(false);

        if (
          isParseXlsxDataError(parsingError) &&
          parsingError.reason === "templateError"
        ) {
          setLoading(false);
          return addToast({
            message: t("errors.invalidTemplate.message"),
            title: t("errors.invalidTemplate.title"),
          });
        }

        return addToast({
          message: t("errors.fileParseError.message"),
          title: t("errors.fileParseError.title"),
        });
      }

      if (parsedData.length === 0) {
        setLoading(false);
        return addToast({
          message: t("errors.fileEmpty.message"),
          title: t("errors.fileEmpty.title"),
        });
      }

      const hasFlowColumn = parsedData.some((row) => !isEmpty(row.flow));
      const hasPowerColumn = parsedData.some((row) => !isEmpty(row.power));
      const hasDeltaTColumn = parsedData.some(
        (row) => !isEmpty(row.differentialTemperature),
      );
      let powerOrFlow = useFlowOrPower;
      setLoading(false);

      if (useFlowOrPower === "flow") {
        if (!hasFlowColumn && (hasPowerColumn || hasDeltaTColumn)) {
          const confirmation = await confirm({
            cancelLabel: t("confirm.switchToPowerMode.cancel"),
            confirmLabel: t("confirm.switchToPowerMode.confirm"),
            message: t("confirm.switchToPowerMode.message"),
          });
          if (!confirmation) return;
          flushSync(() => {
            setUseFlowOrPower("power");
            powerOrFlow = "power";
          });
        }
      } else if (useFlowOrPower === "power") {
        if (!hasPowerColumn && !hasDeltaTColumn && hasFlowColumn) {
          const confirmation = await confirm({
            cancelLabel: t("confirm.switchToFlowMode.cancel"),
            confirmLabel: t("confirm.switchToFlowMode.confirm"),
            message: t("confirm.switchToFlowMode.message"),
          });
          if (!confirmation) return;
          flushSync(() => {
            setUseFlowOrPower("flow");
            powerOrFlow = "flow";
          });
        }
      }

      // Ensure that all columns with data are visible in the table
      const visibleColumns = header.table.getVisibleLeafColumns();
      const visibleColumnIds = visibleColumns.map((column) => column.id);
      const columnsWithData = unique(
        parsedData.flatMap((row) =>
          batchInputColumns.filter((key) => !isEmpty(row[key])),
        ),
      );
      const difference = columnsWithData.filter(
        (key) => !visibleColumnIds.includes(key),
      );
      const allLeafColumns = header.table.getAllLeafColumns();
      for (const key of difference) {
        const column = allLeafColumns.find((col) => col.id === key);
        column?.toggleVisibility?.(true);
      }

      dispatch(
        replaceAllValues({
          buildSuggestionOutput,
          categoryId,
          convertToInternationalUnit,
          convertToUserUnit,
          data: parsedData,
          fetchSuggestions,
          getProductDescriptions,
          useFlowOrPower: powerOrFlow,
          waitUntilReady,
          waterCharacteristics: waterCharacteristicsData,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      addToast,
      categoryId,
      convertToInternationalUnit,
      convertToUserUnit,
      fetchSuggestions,
      useFlowOrPower,
      dispatch,
      units,
      waterCharacteristicsData,
    ],
  );

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    disabled: loading || waterCharacteristicsData === undefined,
    multiple: false,
    onDrop,
  });

  return (
    <>
      <Dropzone
        aria-label={t("dropzone.ariaLabel")}
        className={styles.ImportDropzone}
        data-error={waterCharacteristics.status === "error" || undefined}
        dragging={isDragActive}
        inputProps={getInputProps()}
        loading={loading || waterCharacteristics.status === "loading"}
        rootProps={getRootProps()}
      >
        <DropzoneLoading>
          <LoadingSpinner />
        </DropzoneLoading>

        <DropzoneDragging as="p">
          {t("dropzone.contentDragging")}
        </DropzoneDragging>

        <DropzoneContent>
          {waterCharacteristics.status === "error" ? (
            <>
              <TriangleIcon />
              <p>
                <FieldError errors={waterCharacteristics.messages.join("\n")} />
              </p>
            </>
          ) : (
            <>
              <ImportXlsxIcon />
              <p>
                <Trans
                  components={[<br />, <u />]}
                  i18nKey="product:selection.batch.import.dropzone.content"
                />
              </p>
            </>
          )}
        </DropzoneContent>
      </Dropzone>
      {dialog}
    </>
  );
}
